import { Button, InputAdornment, TextField, TextFieldProps } from '@mui/material';
import { useI18n } from 'iomart-hooks';
import { useFormContext, Controller } from 'react-hook-form';
import Iconify from 'src/components/iconify';

type Props = TextFieldProps & {
  name: string;
  accept?: string;
  noT?: boolean;
};

const DEFAULT_ACCEPT = 'image/png,image/jpeg,image/webp';

const fileToDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });

export default function RHFImageBase64Field({ name, label, noT, helperText, accept, ...other }: Props) {
  const { control, setValue } = useFormContext();
  const { t } = useI18n(false);
  const inputProps = other.InputProps || {};
  const fileAccept = accept || DEFAULT_ACCEPT;

  const uploadAdornment = (
    <InputAdornment position="end">
      <Button component="label" size="small" sx={{ minWidth: 0, px: 1 }}>
        <Iconify icon="wpf:view-file" width={16} />
        <input
          hidden
          type="file"
          accept={fileAccept}
          onChange={async (event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (!file) return;
            try {
              const dataUrl = await fileToDataUrl(file);
              if (dataUrl) {
                setValue(name, dataUrl, { shouldDirty: true, shouldValidate: true });
              }
            } catch {
              // noop
            }
          }}
        />
      </Button>
    </InputAdornment>
  );

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          label={noT ? label : t(label)}
          sx={{
            '& .MuiOutlinedInput-input': {
              font: 'menu',
            },
          }}
          fullWidth
          value={typeof field.value === 'number' && field.value === 0 ? '' : field.value}
          error={!!error}
          helperText={error ? error?.message : helperText}
          size="small"
          InputLabelProps={{ shrink: true }}
          {...other}
          InputProps={{
            ...inputProps,
            endAdornment: (
              <>
                {inputProps.endAdornment}
                {uploadAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
